import { useAuth } from "@/hooks/use-auth";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { apiClient } from "@/lib/api-client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilterCombobox } from "@/components/ui/filter-combobox";
import { FileText, Loader2, Building2, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RekapRemunPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdminFakultas = user?.role === "admin_fakultas";
  const formattedFakultasId = user?.master_fakultas_id
    ? String(user.master_fakultas_id).padStart(2, "0")
    : "all";
  const fktKodeParam = isAdminFakultas
    ? formattedFakultasId
    : searchParams.get("fkt_kode") || "all";

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  const [isExporting, setIsExporting] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [passTte, setPassTte] = useState("");
  const [niu, setNiu] = useState("");

  const handleExportPdf = async () => {
    if (!year || !semester) return;
    if (!passTte || !niu) {
      toast.error("Pass TTE dan NIU harus diisi");
      return;
    }

    setIsExporting(true);
    const loadingId = toast.loading("Mengekspor PDF...");

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;

      const params = new URLSearchParams();
      params.append("year", year.toString());
      params.append("semester", semester.toString());
      params.append("pass_tte", passTte);
      params.append("niu", niu);
      if (fktKodeParam && fktKodeParam !== "all") {
        params.append("fkt_kode", fktKodeParam);
      }

      const exportUrl = `${baseUrl}/api/v1/dosen-remun/analytics-70/export-pdf?${params.toString()}`;

      const response = await apiClient<any>(exportUrl);

      if (response?.url) {
        toast.success("PDF berhasil diekspor! Membuka dalam 5 detik...", {
          id: loadingId,
        });
        setIsExportDialogOpen(false);
        setPassTte("");
        setNiu("");

        setTimeout(() => {
          window.open(response.url, "_blank");
        }, 5000);
      } else {
        throw new Error("Gagal mendapatkan URL PDF");
      }
    } catch (error: any) {
      toast.error(error.message || "Gagal mengekspor PDF", { id: loadingId });
    } finally {
      setIsExporting(false);
    }
  };

  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const { data: fakultasData, isLoading: isLoadingFakultas } = useQuery({
    queryKey: ["master-fakultas-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-fakultas?page=1&limit=1000`,
      );
    },
  });

  const {
    data: rekapDataResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["rekap-remun", { year, semester, fkt_kode: fktKodeParam }],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/dosen-remun/analytics-70/rekap?year=${year}&semester=${semester}&fkt_kode=${fktKodeParam}`,
      );
    },
    enabled: !!year && !!semester && fktKodeParam !== "all",
  });

  const rekapData = rekapDataResponse?.data;
  const akumulasi = rekapData?.akumulasi || [];
  const detail = rekapData?.detail || [];

  return (
    <div className="container mx-auto py-8 space-y-6">
      <Card className="shadow-xl border-slate-200/60 bg-white/50 backdrop-blur-xl rounded-xl">
        <CardHeader className="p-6 sm:p-8 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2 text-slate-900">
              <FileText className="w-6 h-6 text-primary/80" />
              Rekap Remun
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1.5 text-base">
              Rekapitulasi akumulasi dan detail perhitungan remunerasi dosen.
            </CardDescription>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-auto">
            {/* Fakultas Filter */}
            <div className="w-full sm:w-72">
              <FilterCombobox
                value={fktKodeParam}
                onValueChange={(val) =>
                  updateParams({
                    fkt_kode: val === "all" ? "" : val,
                  })
                }
                options={[
                  { value: "all", label: "Semua Fakultas" },
                  ...(fakultasData?.datas || []).map((f: any) => ({
                    value: f.kode_fakultas,
                    label: f.nama_fakultas,
                  })),
                ]}
                placeholder="Pilih Fakultas..."
                searchPlaceholder="Cari Fakultas..."
                emptyText="Fakultas tidak ditemukan."
                disabled={isAdminFakultas || isLoadingFakultas}
              />
            </div>

            <Dialog
              open={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  disabled={
                    isExporting ||
                    isLoading ||
                    (fktKodeParam === "all" && !isAdminFakultas)
                  }
                  className="gap-2 shrink-0 bg-primary hover:bg-primary/90 text-white"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export PDF</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-106.25">
                <DialogHeader>
                  <DialogTitle>Export PDF (TTE)</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="niu">NIU</Label>
                    <Input
                      id="niu"
                      value={niu}
                      onChange={(e) => setNiu(e.target.value)}
                      placeholder="Masukkan NIU"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="pass_tte">Passphrase TTE</Label>
                    <Input
                      id="pass_tte"
                      type="password"
                      value={passTte}
                      onChange={(e) => setPassTte(e.target.value)}
                      placeholder="Masukkan Passphrase TTE"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsExportDialogOpen(false)}
                    disabled={isExporting}
                  >
                    Batal
                  </Button>
                  <Button onClick={handleExportPdf} disabled={isExporting}>
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Generate PDF
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* State rendering */}
      {fktKodeParam === "all" && !isAdminFakultas ? (
        <Card className="border-dashed border-2 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Building2 className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600">Pilih Fakultas</p>
            <p className="text-sm">
              Silakan pilih fakultas terlebih dahulu untuk melihat rekap data.
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card className="border shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <p className="text-sm font-medium">Memuat data rekapitulasi...</p>
          </CardContent>
        </Card>
      ) : isError ? (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="flex flex-col items-center justify-center py-12 text-red-500">
            <p className="text-sm font-medium">
              Gagal memuat data. Silakan coba lagi.
            </p>
          </CardContent>
        </Card>
      ) : rekapData ? (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="shadow-sm">
              <CardContent className="p-4 flex flex-col justify-center">
                <p className="text-sm font-medium text-slate-500">Fakultas</p>
                <p
                  className="text-lg font-bold text-slate-900 line-clamp-1"
                  title={rekapData.fakultas}
                >
                  {rekapData.fakultas}
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-4 flex flex-col justify-center">
                <p className="text-sm font-medium text-slate-500">
                  Tahun Akademik
                </p>
                <p className="text-lg font-bold text-slate-900">
                  {rekapData.tahun}
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-slate-500 mb-1">
                  Semester
                </p>
                <Badge
                  variant="outline"
                  className="border-primary/20 text-primary bg-primary/5 uppercase font-bold"
                >
                  {rekapData.semester}
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Akumulasi */}
          <Card className="shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-4">
              <CardTitle className="text-lg">Akumulasi Bulanan</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold text-slate-900 border-r w-50 text-center">
                        KOMPONEN
                      </TableHead>
                      {akumulasi.map((item: any, idx: number) => (
                        <TableHead
                          key={idx}
                          className="text-center font-semibold text-slate-900 border-r whitespace-nowrap min-w-30"
                        >
                          {item.bulan.substring(0, 3)}{" "}
                          {item.bulan.split(" ")[1]}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {akumulasi.length > 0 ? (
                      <>
                        <TableRow>
                          <TableCell className="font-medium border-r">
                            Point Position
                          </TableCell>
                          {akumulasi.map((item: any, idx: number) => (
                            <TableCell
                              key={idx}
                              className="text-right border-r"
                            >
                              {new Intl.NumberFormat("id-ID", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total_point_position)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium border-r">
                            Unsur P3
                          </TableCell>
                          {akumulasi.map((item: any, idx: number) => (
                            <TableCell
                              key={idx}
                              className="text-right border-r"
                            >
                              {new Intl.NumberFormat("id-ID", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total_unsur_p3)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium border-r">
                            Unsur Penunjang
                          </TableCell>
                          {akumulasi.map((item: any, idx: number) => (
                            <TableCell
                              key={idx}
                              className="text-right border-r"
                            >
                              {new Intl.NumberFormat("id-ID", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total_unsur_penunjang)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium border-r">
                            IKU
                          </TableCell>
                          {akumulasi.map((item: any, idx: number) => (
                            <TableCell
                              key={idx}
                              className="text-right border-r"
                            >
                              {new Intl.NumberFormat("id-ID", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total_iku)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow className="bg-slate-50/50">
                          <TableCell className="font-bold text-slate-900 border-r">
                            Total
                          </TableCell>
                          {akumulasi.map((item: any, idx: number) => (
                            <TableCell
                              key={idx}
                              className="text-right font-bold text-slate-900 border-r"
                            >
                              {new Intl.NumberFormat("id-ID", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total_all)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={1}
                          className="text-center py-6 text-slate-500"
                        >
                          Tidak ada data akumulasi
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Detail */}
          {detail.length > 0 && (
            <Card className="shadow-sm overflow-hidden">
              <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-4">
                <CardTitle className="text-lg">
                  Detail Capaian Dosen per Bulan
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <Tabs defaultValue={detail[0].bulan} className="w-full">
                  <div className="overflow-x-auto pb-2 custom-scrollbar">
                    <TabsList className="w-full justify-start inline-flex h-11 items-center rounded-md bg-slate-100 p-1 text-slate-500">
                      {detail.map((item: any) => (
                        <TabsTrigger
                          key={item.bulan}
                          value={item.bulan}
                          className="min-w-fit px-4"
                        >
                          {item.bulan}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  {detail.map((item: any) => (
                    <TabsContent
                      key={item.bulan}
                      value={item.bulan}
                      className="mt-4 focus-visible:outline-none"
                    >
                      <div className="rounded-md border overflow-hidden">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="w-16 text-center font-semibold text-slate-900">
                                No
                              </TableHead>
                              <TableHead className="font-semibold text-slate-900 w-48">
                                NIP
                              </TableHead>
                              <TableHead className="font-semibold text-slate-900">
                                Nama Dosen
                              </TableHead>
                              <TableHead className="text-right font-semibold text-slate-900">
                                Total Poin
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {item.dosen.map((dsn: any, idx: number) => (
                              <TableRow key={dsn.nip}>
                                <TableCell className="text-center">
                                  {idx + 1}
                                </TableCell>
                                <TableCell className="font-medium text-slate-600">
                                  {dsn.nip}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {dsn.nama}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-primary">
                                  {new Intl.NumberFormat("id-ID", {
                                    maximumFractionDigits: 2,
                                  }).format(dsn.total_point)}
                                </TableCell>
                              </TableRow>
                            ))}
                            {item.dosen.length === 0 && (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  className="text-center py-6 text-slate-500"
                                >
                                  Tidak ada data dosen
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}
    </div>
  );
}
